// ══════════════════════════════════════════════════════════════════════════════════════════════
//  «TIEMPO CUMPLIDO» · LA PANTALLA PARPADEA Y SALE EL AVISO CON SU «PARAR»                     (t271, 16-oct-2026)
// ══════════════════════════════════════════════════════════════════════════════════════════════
//
//  Lo monta la raíz de la app (context/RelojContext.jsx) cuando la cuenta atrás llega a cero, esté donde esté la persona:
//  con la ventana del reloj cerrada, en otra página o con la pantalla bloqueada.
//
//   · LA PANTALLA ENTERA PARPADEA con el color de la clínica (`--primary`) y el de aviso (`--warning`), por encima de todo
//     y SIN TAPAR LOS CLICS (`pointer-events: none`): se puede seguir trabajando debajo. Menos de tres destellos por
//     segundo, que es el límite de las pautas de accesibilidad para no molestar a quien es sensible a los destellos.
//     t271c (16-oct-2026) · el dueño: «el aviso de color cuando acaba la cuenta atrás debe ser más largo». Sigue hasta que
//     se pulsa «Parar» o «+1 min» (que quitan este aviso), con un tope de un minuto (`DURACION_DESTELLO_MS`): los primeros
//     segundos fuerte (`DESTELLO_FUERTE_MS`), y después un latido suave del color de la clínica, más lento, que no molesta
//     y sigue a la vista.
//   · Con «reducir movimiento» en el sistema (prefers-reduced-motion) NO PARPADEA: un fondo de color fijo, suave, hasta que
//     se pulsa (`modoDestello`, utils/relojAviso.js). Y por si el navegador lo cambia sobre la marcha, la hoja de abajo
//     también apaga la animación con la misma consulta.
//   · Si la pestaña estaba escondida al terminar, el parpadeo espera a que se vuelva a ella: un destello que nadie ve no
//     avisa de nada (mientras tanto avisan el título de la pestaña y la notificación).
//   · EL AVISO, GRANDE, en el centro: «Tiempo cumplido», lo que duraba y a qué hora terminó, «Parar» y «+1 min». No se
//     lleva el foco (quien está escribiendo una nota no pierde lo que teclea); Escape también lo para.
import { useEffect, useState } from 'react'
import { modoDestello, TXT_TIEMPO_CUMPLIDO } from '../../utils/relojAviso'
import { duracionEnPalabras } from '../../utils/reloj'
import { t } from '../../utils/i18n'   // B-2259 · los textos, en el idioma de quien mira (utils/i18n)

// Lo fuerte (cinco vueltas de 1,2 s) y lo suave después (de 2 s), hasta el minuto.
export const DESTELLO_FUERTE_MS = 6000
export const DURACION_DESTELLO_MS = 60000
// Por encima de todo lo de la app (el bloqueo de pantalla y los avisos van a 99999).
export const Z_DESTELLO = 100000

export const CSS_DESTELLO = [
  '@keyframes biango-reloj-destello {',
  '  0%, 40%, 80%, 100% { background: transparent; }',
  '  20% { background: color-mix(in srgb, var(--primary) 55%, transparent); }',
  '  60% { background: color-mix(in srgb, var(--warning) 50%, transparent); }',
  '}',
  '@keyframes biango-reloj-destello-suave {',
  '  0%, 100% { background: transparent; }',
  '  50% { background: color-mix(in srgb, var(--primary) 40%, transparent); }',
  '}',
  // Fuerte: 1,2 s por vuelta con dos destellos (1,7 por segundo), cinco vueltas = DESTELLO_FUERTE_MS. Suave: empieza al
  // acabar la fuerte (su retraso; `forwards` y no `both`, para que no tape la fuerte mientras espera) y da 27 vueltas de
  // 2 s, medio latido por segundo, hasta el minuto de DURACION_DESTELLO_MS.
  '.reloj-destello[data-destello="parpadeo"] { animation: biango-reloj-destello 1.2s linear 5 both, biango-reloj-destello-suave 2s ease-in-out 6s 27 forwards; }',
  '@media (prefers-reduced-motion: reduce) {',
  '  .reloj-destello[data-destello="parpadeo"] { animation: none; background: color-mix(in srgb, var(--primary) 26%, transparent); }',
  '}',
].join('\n')

const dos = (n) => String(n).padStart(2, '0')
function horaDe(ms) {
  try { const d = new Date(ms); return dos(d.getHours()) + ':' + dos(d.getMinutes()) } catch (_) { return '' }
}

export default function AvisoTiempoCumplido({ aviso, onParar, onMasMinuto, ventana }) {
  const w = ventana || (typeof window !== 'undefined' ? window : null)
  const doc = w && w.document
  const modo = modoDestello(w)
  const escondida = () => { try { return !!(doc && doc.hidden) } catch (_) { return false } }
  // Desde cuándo se ve el destello: ya, o al volver a la pestaña si estaba escondida.
  const [visto, setVisto] = useState(() => !escondida())
  const [acabado, setAcabado] = useState(false)

  useEffect(() => {
    if (visto || !doc || typeof doc.addEventListener !== 'function') return undefined
    const mirar = () => { if (!escondida()) setVisto(true) }
    doc.addEventListener('visibilitychange', mirar)
    return () => doc.removeEventListener('visibilitychange', mirar)
  }, [visto])

  // El parpadeo sigue hasta «Parar» o «+1 min» (que quitan el aviso entero) y, como mucho, un minuto; el color fijo se queda
  // mientras dura el aviso.
  useEffect(() => {
    if (!visto || modo === 'fijo') return undefined
    const id = setTimeout(() => setAcabado(true), DURACION_DESTELLO_MS)
    return () => clearTimeout(id)
  }, [visto, modo])

  useEffect(() => {
    if (!w || typeof w.addEventListener !== 'function') return undefined
    const tecla = (e) => { if (e && e.key === 'Escape' && typeof onParar === 'function') onParar() }
    w.addEventListener('keydown', tecla)
    return () => w.removeEventListener('keydown', tecla)
  }, [onParar])

  const conDestello = visto && (modo === 'fijo' || !acabado)
  const dura = aviso && aviso.total ? duracionEnPalabras(aviso.total) : ''
  const hora = aviso && aviso.fin ? horaDe(aviso.fin) : ''

  return (
    <>
      <style>{CSS_DESTELLO}</style>
      {conDestello && (
        <div className="reloj-destello" data-destello={modo} aria-hidden="true"
          style={{ position: 'fixed', inset: 0, zIndex: Z_DESTELLO, pointerEvents: 'none',
            background: modo === 'fijo' ? 'color-mix(in srgb, var(--primary) 26%, transparent)' : 'transparent' }} />
      )}
      <div role="alertdialog" aria-modal="false" aria-live="assertive" aria-labelledby="reloj-aviso-titulo" aria-describedby="reloj-aviso-texto"
        data-aviso-reloj={aviso ? String(aviso.id) : ''}
        style={{ position: 'fixed', zIndex: Z_DESTELLO + 1, left: '50%', top: '50%', transform: 'translate(-50%, -50%)',
          width: 'min(420px, calc(100vw / var(--z, 1) - 32px))', boxSizing: 'border-box', padding: '24px 22px 20px',
          background: 'var(--surface)', color: 'var(--text-primary)', borderRadius: '18px', border: '1px solid var(--border)',
          borderTop: '6px solid var(--primary)', boxShadow: 'var(--shadow-lg)', textAlign: 'center' }}>
        <svg width="44" height="44" viewBox="0 0 24 24" fill="none" stroke="var(--primary)" strokeWidth="1.9" strokeLinecap="round"
          strokeLinejoin="round" aria-hidden="true" focusable="false" style={{ display: 'block', margin: '0 auto 8px' }}>
          <circle cx="12" cy="13.5" r="7.5" />
          <path d="M12 10v3.5l2.4 1.6" />
          <path d="M9.5 3h5" />
          <path d="M12 3v3" />
        </svg>
        <div id="reloj-aviso-titulo" style={{ fontSize: '28px', fontWeight: 800, lineHeight: 1.15, letterSpacing: '-0.01em' }}>{TXT_TIEMPO_CUMPLIDO}</div>
        <div id="reloj-aviso-texto" style={{ marginTop: '8px', fontSize: '14px', color: 'var(--text-secondary)', lineHeight: 1.45 }}>
          {dura ? t('components.reloj.AvisoTiempoCumplido.la_cuenta_atras_de_ha', 'La cuenta atrás de {{dura}} ha terminado{{x}}.', { dura, x: hora ? t('components.reloj.AvisoTiempoCumplido.a_las', ' a las {{hora}}', { hora }) : '' }) : t('components.reloj.AvisoTiempoCumplido.la_cuenta_atras_ha_terminado', 'La cuenta atrás ha terminado.')}
        </div>
        <div style={{ display: 'flex', gap: '10px', justifyContent: 'center', marginTop: '18px', flexWrap: 'wrap' }}>
          <button type="button" data-reloj-parar="" onClick={onParar}
            style={{ minWidth: '150px', padding: '12px 22px', borderRadius: '12px', border: '1px solid var(--primary)', background: 'var(--primary)',
              color: 'var(--sobre-primary)', fontSize: '16px', fontWeight: 800, cursor: 'pointer' }}>
            {t('components.reloj.AvisoTiempoCumplido.parar', 'Parar')}
          </button>
          <button type="button" data-reloj-mas-minuto="" onClick={onMasMinuto}
            style={{ padding: '12px 18px', borderRadius: '12px', border: '1px solid var(--border)', background: 'var(--surface-2)',
              color: 'var(--text-primary)', fontSize: '15px', fontWeight: 700, cursor: 'pointer' }}>
            {t('components.reloj.AvisoTiempoCumplido.1_min', '+1 min')}
          </button>
        </div>
      </div>
    </>
  )
}
